import type { AllocationRiskProfile } from '../../../expert-review/types/allocation.types'
import {
  LumpsumPlansPlanType,
  LumpsumPlanStatus,
  type LumpsumFund,
  type LumpsumIdealAllocation,
  type LumpsumRecommendationActionType,
  type LumpsumRecommendationStatus,
  type LumpsumRecommendationSubType,
  type LumpsumRecommendationType
} from './common.types'

export enum LumpsumAllocationCategoryExecutionStatus {
  ACTIONABLE_INCREASE = 'ACTIONABLE_INCREASE',
  ACTIONABLE_DECREASE = 'ACTIONABLE_DECREASE',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ALREADY_IDEAL = 'ALREADY_IDEAL'
}

/** Backend `tags` values on allocation list recommendations. */
export enum LumpsumAllocationInsightTag {
  // Sell
  MISSED_GAINS = 'MISSED_GAINS',
  POOR_PERFORMANCE = 'POOR_PERFORMANCE',
  INSIGNIFICANT_ALLOCATION = 'INSIGNIFICANT_ALLOCATION',
  TOO_MANY_FUNDS = 'TOO_MANY_FUNDS',
  CATEGORY_NOT_RECOMMENDED = 'CATEGORY_NOT_RECOMMENDED',
  HIGH_EXPOSURE = 'HIGH_EXPOSURE',
  CONCENTRATION_RISK = 'CONCENTRATION_RISK',
  NEAR_CONCENTRATION_RISK = 'NEAR_CONCENTRATION_RISK',
  REALLOCATED_PREFERENCE = 'REALLOCATED_PREFERENCE',
  // Buy
  TOP_RANKED_FUND = 'TOP_RANKED_FUND',
  INCREASES_EXPOSURE = 'INCREASES_EXPOSURE',
  IMPROVES_DIVERSIFICATION = 'IMPROVES_DIVERSIFICATION',
  IMPROVES_PERFORMANCE = 'IMPROVES_PERFORMANCE',
  // Under review
  PARTIAL_SELL_LOCKIN = 'PARTIAL_SELL_LOCKIN',
  PARTIAL_SELL_TAX = 'PARTIAL_SELL_TAX'
}

export function isLumpsumAllocationExecutionStatusActionable(
  executionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
): boolean {
  return (
    executionStatus === LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE ||
    executionStatus === LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE
  )
}

export interface LumpsumIdealAllocationFundCount {
  absolute?: number | null
  minimum?: number | null
  maximum?: number | null
}

export interface LumpsumAllocationCategoryFundMovement {
  executionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  currentFundCount?: number | null
  idealAllocationFundCount?: LumpsumIdealAllocationFundCount | null
}

export interface LumpsumAllocationCategoryAllocationMovement {
  executionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  currentAllocationPercentage?: number | null
  idealAllocationPercentage?: LumpsumIdealAllocation | null
}

export interface LumpsumAllocationSellRecommendation extends LumpsumFund {
  actionType?: LumpsumRecommendationActionType | null
  recommendationType?: LumpsumRecommendationType | null
  recommendationStatus?: LumpsumRecommendationStatus | null
  recommendationStatusReason?: string | null
  recommendationStatusDisplayText?: string | null
  executionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  sellAmount?: number | null
  buyAmount?: number | null
  currentAllocationPercentage?: number | null
  idealAllocationPercentage?: LumpsumIdealAllocation | null
  missedGains?: number | null
  tags?: LumpsumAllocationInsightTag[] | null
}

export interface LumpsumAllocationNoActionFund extends LumpsumFund {
  missedGains?: number | null
  tags?: LumpsumAllocationInsightTag[] | null
}

export interface LumpsumAllocationBuyRecommendation extends LumpsumFund {
  actionType?: LumpsumRecommendationActionType | null
  recommendationType?: LumpsumRecommendationType | null
  recommendationStatus?: LumpsumRecommendationStatus | null
  recommendationStatusReason?: string | null
  recommendationStatusDisplayText?: string | null
  executionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  sellAmount?: number | null
  buyAmount?: number | null
  currentAllocationPercentage?: number | null
  idealAllocationPercentage?: LumpsumIdealAllocation | null
  tags?: LumpsumAllocationInsightTag[] | null
}

export interface LumpsumAllocationUnderReviewRecommendation extends LumpsumFund {
  actionType?: LumpsumRecommendationActionType | null
  recommendationType?: LumpsumRecommendationType | null
  recommendationStatus?: LumpsumRecommendationStatus | null
  recommendationStatusReason?: string | null
  recommendationStatusDisplayText?: string | null
  executionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  sellAmount?: number | null
  buyAmount?: number | null
  currentAllocationPercentage?: number | null
  idealAllocationPercentage?: LumpsumIdealAllocation | null
  underReviewReason?: string | null
  underReviewReasonText?: string | null
  tags?: LumpsumAllocationInsightTag[] | null
}

export interface LumpsumAllocationExecutedRecommendation extends LumpsumFund {
  actionType?: LumpsumRecommendationActionType | null
  recommendationType?: LumpsumRecommendationType | null
  recommendationStatus?: LumpsumRecommendationStatus | null
  recommendationStatusReason?: string | null
  recommendationStatusDisplayText?: string | null
  executionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  sellAmount?: number | null
  buyAmount?: number | null
  currentAllocationPercentage?: number | null
  idealAllocationPercentage?: LumpsumIdealAllocation | null
  underReviewReason?: string | null
  missedGains?: number | null
  tags?: LumpsumAllocationInsightTag[] | null
}

export interface LumpsumAllocationInvalidatedRecommendation extends LumpsumFund {
  actionType?: LumpsumRecommendationActionType | null
  recommendationSubType?: LumpsumRecommendationSubType | null
  recommendationType?: LumpsumRecommendationType | null
  recommendationStatus?: LumpsumRecommendationStatus | null
  recommendationStatusReason?: string | null
  recommendationStatusDisplayText?: string | null
  missedGains?: number | null
  tags?: LumpsumAllocationInsightTag[] | null
}

export interface LumpsumAllocationCategory {
  category?: string | null
  derivedCategory?: string | null
  categoryName?: string | null
  group?: string | null
  segment?: string | null
  fundMovement?: LumpsumAllocationCategoryFundMovement | null
  allocationMovement?: LumpsumAllocationCategoryAllocationMovement | null
  sellRecommendations?: LumpsumAllocationSellRecommendation[] | null
  buyRecommendations?: LumpsumAllocationBuyRecommendation[] | null
  underReviewRecommendations?: LumpsumAllocationUnderReviewRecommendation[] | null
  executedRecommendations?: LumpsumAllocationExecutedRecommendation[] | null
  invalidatedRecommendations?: LumpsumAllocationInvalidatedRecommendation[] | null
  noActionFunds?: LumpsumAllocationNoActionFund[] | null
}

export interface LumpsumAllocationPlansResponse {
  userId?: string | null
  planType?: LumpsumPlansPlanType.ALLOCATION | null
  timeToPlanDrop?: number | null
  isPlanEnabled?: boolean | null
  /** Prime (or relevant) subscription for allocation. */
  isPlanSubscriptionEligible?: boolean | null
  planStatus?: LumpsumPlanStatus | null
  planCount?: number | null
  transactionCta?: unknown
  actionableFundCount?: number | null
  underReviewFundCount?: number | null
  invalidatedFundCount?: number | null
  sellFundCount?: number | null
  buyFundCount?: number | null
  idealFundCount?: number | null
  includedFundCount?: number | null
  excludedFundCount?: number | null
  newRecommendationsCount?: number | null
  updatedRecommendationsCount?: number | null
  invalidatedRecommendationsCount?: number | null
  planSnapshotDate?: number | null
  planDurationInMonths?: number | null
  riskProfile?: AllocationRiskProfile | null
  /** Native bridge CTA to start / resume strategy questionnaire. */
  strategyCta?: unknown
  /** Native bridge CTA that resets the allocation strategy. */
  resetAllocation?: unknown
  /** Native bridge CTA to strategy landing. */
  strategyLandingCta?: unknown
  /** Native bridge CTA for slot bottomsheet. */
  slotBottomsheetCta?: unknown
  /** Native bridge CTA for core segment bottomsheet. */
  coreBottomsheetCta?: unknown
  categories?: LumpsumAllocationCategory[] | null
}
